import { createPermissionPayload, Role, RoleAssignment } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { iso639, Language } from '@adornis/internationalization/language-iso639-2.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { changeUserPassword } from '@adornis/users/server/password.js';
import { createNewUser } from '@adornis/users/server/util.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { LMSCourseEnrollment } from '@nsc/simple-lms/entities/LMSCourseEnrollment.js';
import { ensureAppUserForFieldEngineer } from '@nsc/smartteams-shared/api/create-app-user-for-fe.js';
import { normalizedEmailFilter, normalizeEmail } from '@nsc/smartteams-shared/api/email-filter.js';
import fastCsv from 'fast-csv';
import { SmartTeamsPartnerCompany } from '../db/SmartTeamsCompany.js';
import { SmartTeamsFieldEngineer } from '../db/SmartTeamsFieldEngineer.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { SmartTeamsPermission } from '../features/permissions/roles-and-permissions.js';
import { parseEngineerType } from './parse-engineer-type.js';
import { sendFieldEngineerOnboardingEmail } from './send-field-engineer-onboarding-email.js';

type CsvRecord = {
  partnerCompanyName: string;
  firstName: string;
  lastName: string;
  email: string;
  city: string;
  country: string;
  localLanguage: string;
  englishLanguage: string;
  password: string;
  serviceNowSysId: string;
  /** Raw `Engineer Type` cell: `partner`, `internal` or blank. Only `partner` is acted on. */
  engineerType: string;
};

function normalizeCellValue(value: string | null | undefined): string {
  return (value ?? '').replace(/\u00A0/g, ' ').trim();
}

function languageByEnglishName(languageName: string): keyof typeof Language | undefined {
  const normalized = languageName.trim().toLocaleLowerCase();
  if (!normalized || normalized === 'na' || normalized === 'n/a' || normalized === 'none') return undefined;

  const exact = iso639.find(language => language.english.toLocaleLowerCase() === normalized);
  if (exact) return exact.enumCode;

  const partial = iso639.find(language => language.english.toLocaleLowerCase().includes(normalized));
  return partial?.enumCode;
}

async function parseCsvRecords(csvContent: string): Promise<CsvRecord[]> {
  return new Promise((resolve, reject) => {
    const records: CsvRecord[] = [];

    const csvStream = fastCsv.parseString(csvContent, { headers: true });

    csvStream.on('data', (row: { [key: string]: string }) => {
      const normalizedRow: { [key: string]: string } = {};
      Object.keys(row).forEach(key => {
        normalizedRow[key.toLocaleLowerCase().trim()] = normalizeCellValue(row[key]);
      });

      const headerIndex = {
        partnerCompanyName: 'partner company',
        firstName: 'resource first name',
        lastName: 'resource last name',
        email: 'resource e-mail address',
        city: 'city',
        country: 'country',
        localLanguage: 'local language',
        englishLanguage: 'english language',
        password: 'password',
      };

      // Optional columns: absent from the original template, so their absence must not fail the
      // import of files produced before they existed.
      const optionalHeaderIndex = { serviceNowSysId: 'servicenow id', engineerType: 'engineer type' };

      const missingHeaders = Object.values(headerIndex).filter(header => !(header in normalizedRow));
      if (missingHeaders.length > 0) {
        csvStream.destroy(new Error(`CSV header is missing required columns: ${missingHeaders.join(', ')}`));
        return;
      }

      const record: CsvRecord = {
        partnerCompanyName: normalizedRow[headerIndex.partnerCompanyName] || '',
        firstName: normalizedRow[headerIndex.firstName] || '',
        lastName: normalizedRow[headerIndex.lastName] || '',
        email: normalizedRow[headerIndex.email] || '',
        city: normalizedRow[headerIndex.city] || '',
        country: normalizedRow[headerIndex.country] || '',
        localLanguage: normalizedRow[headerIndex.localLanguage] || '',
        englishLanguage: normalizedRow[headerIndex.englishLanguage] || '',
        password: normalizedRow[headerIndex.password] || '',
        serviceNowSysId: normalizedRow[optionalHeaderIndex.serviceNowSysId] || '',
        engineerType: normalizedRow[optionalHeaderIndex.engineerType] || '',
      };

      // Only add record if it has actual data (skip empty rows)
      if (record.email || record.firstName || record.lastName) {
        records.push(record);
      }
    });

    csvStream.on('end', () => {
      resolve(records);
    });

    csvStream.on('error', (error: Error) => {
      reject(error);
    });
  });
}

export const importFieldEngineersCsv = registerMutation({
  operationName: 'importFieldEngineersCsv',
  type: () => [String],
  params: [
    { name: 'csvContent', type: () => String },
    { name: 'courseId', type: () => String },
  ],
  resolve: (csvContent: string, courseId?: string) => async () => {
    const canImport = await accessController.can(createPermissionPayload(SmartTeamsPermission.FEImportExportAdmin));
    if (!canImport) {
      throw new ExpectedError('No permission to import or export field engineers');
    }

    const records = await parseCsvRecords(csvContent);
    if (!records.length) {
      return ['No data rows found in CSV'];
    }

    const companyCollection = await getRawCollection<SmartTeamsPartnerCompany>(
      SmartTeamsPartnerCompany._collectionName,
    );
    const fieldEngineerCollection = await getRawCollection<SmartTeamsFieldEngineer>(
      SmartTeamsFieldEngineer._collectionName,
    );
    const userCollection = await getRawCollection<AdornisUser>(AdornisUser._collectionName);
    const enrollmentCollection = await getRawCollection<LMSCourseEnrollment>(LMSCourseEnrollment._collectionName);

    const companies = await companyCollection.find({ archived: { $ne: true } }).toArray();
    const companyIdByName = new Map(
      companies.map(company => [normalizeCellValue(company.name).toLocaleLowerCase(), company._id] as const),
    );

    let createdCount = 0;
    let updatedCount = 0;
    let skippedCount = 0;
    const messages: string[] = [];

    for (const [rowIndex, record] of records.entries()) {
      const rowNumber = rowIndex + 2;

      if (!record.email) {
        skippedCount += 1;
        messages.push(`Row ${rowNumber}: skipped (missing email)`);
        continue;
      }

      // Canonical email for this row. Entity hooks (AdornisUser, field engineer) and the
      // app-user upsert lowercase what they persist, but the AdornisUser username is set from
      // this value directly and is not hook-covered — so normalize once here.
      const email = normalizeEmail(record.email);

      const normalizedPartnerCompanyName = normalizeCellValue(record.partnerCompanyName);
      if (!normalizedPartnerCompanyName) {
        skippedCount += 1;
        messages.push(`Row ${rowNumber}: skipped (missing partner company)`);
        continue;
      }

      const partnerCompanyKey = normalizedPartnerCompanyName.toLocaleLowerCase();
      let partnerCompanyId = companyIdByName.get(partnerCompanyKey);
      if (!partnerCompanyId) {
        const partnerCompany = new SmartTeamsPartnerCompany({ name: normalizedPartnerCompanyName });

        await partnerCompany.create();
        partnerCompanyId = partnerCompany._id;

        if (partnerCompanyId) {
          companyIdByName.set(partnerCompanyKey, partnerCompanyId);
          messages.push(`Row ${rowNumber}: created partner company (${normalizedPartnerCompanyName})`);
        } else {
          skippedCount += 1;
          messages.push(
            `Row ${rowNumber}: skipped (failed to create partner company: ${normalizedPartnerCompanyName})`,
          );
          continue;
        }
      }

      const matchingFieldEngineers = await fieldEngineerCollection
        .find(normalizedEmailFilter(email))
        .limit(2)
        .toArray();
      if (matchingFieldEngineers.length > 1) {
        skippedCount += 1;
        messages.push(
          `Row ${rowNumber}: skipped (duplicate field engineers differing only in case exist for ${email} and must be merged manually)`,
        );
        continue;
      }
      const existingFieldEngineer = matchingFieldEngineers[0];

      const localLanguage = languageByEnglishName(record.localLanguage);
      const englishLanguageRequested = record.englishLanguage.toLocaleLowerCase() === 'yes';
      const secondLanguage =
        englishLanguageRequested && localLanguage !== Language.english ? Language.english : undefined;
      const displayName = `${record.firstName} ${record.lastName}`.trim();

      // Partner or internal. A bad value is reported and ignored rather than guessed at.
      const engineerType = parseEngineerType(record.engineerType);
      if (engineerType.invalid) {
        messages.push(
          `Row ${rowNumber}: unknown Engineer Type "${record.engineerType}" (use "partner", "internal" or leave blank) — ignored`,
        );
      }

      // Resolve the linked user: reuse an account matching the email, else the field engineer's
      // current link, else create one when a password is supplied. A brand-new field engineer
      // still requires a user, but an existing FE can be updated even without one.
      let userId: string | undefined;
      let passwordApplied = false;
      const matchingUsers = await userCollection.find(normalizedEmailFilter(email)).limit(2).toArray();
      if (matchingUsers.length > 1) {
        skippedCount += 1;
        messages.push(
          `Row ${rowNumber}: skipped (duplicate user accounts differing only in case exist for ${email} and must be merged manually)`,
        );
        continue;
      }
      const existingUser = matchingUsers[0];
      if (existingUser?._id) {
        userId = existingUser._id;
        if (record.password) {
          // Only apply a CSV password to accounts that are (at most) field engineers — never
          // silently reset an account that also holds elevated roles.
          const roleAssignmentCollection = await getRawCollection<RoleAssignment>(RoleAssignment._collectionName);
          const roleAssignment = await roleAssignmentCollection.findOne({ userId });
          const roleCollection = await getRawCollection<Role>(Role._collectionName);
          const fieldEngineerRole = await roleCollection.findOne({ roleName: 'Field Engineer' });
          const hasOnlyFieldEngineerRole =
            !roleAssignment || (roleAssignment.roleIds ?? []).every(roleId => roleId === fieldEngineerRole?._id);

          if (hasOnlyFieldEngineerRole) {
            // Imported passwords are temporary onboarding credentials and are intentionally not
            // run through the password policy validator (see sendFieldEngineerOnboardingEmail).
            await changeUserPassword(userId, record.password, AdornisUser);
            passwordApplied = true;
            messages.push(`Row ${rowNumber}: existing user account — password updated from CSV`);
          } else {
            messages.push(
              `Row ${rowNumber}: existing user account has elevated roles — CSV password NOT applied; use the set-password action`,
            );
          }
        }
      } else if (existingFieldEngineer?.userId) {
        // No account matches the email, but the field engineer is already linked to one.
        userId = existingFieldEngineer.userId;
      } else if (record.password) {
        userId = await createNewUser(new AdornisUser({ username: email, email }), record.password);
        passwordApplied = true;
      } else if (!existingFieldEngineer) {
        skippedCount += 1;
        messages.push(
          `Row ${rowNumber}: skipped (no user found for ${email} and no password provided for user creation)`,
        );
        continue;
      }

      if (existingFieldEngineer) {
        // Upsert: refresh the existing field engineer with the latest CSV data. Fields not
        // present in the import (createdAt, externalId, …) are preserved by spreading the current
        // document. Saving through the entity also runs afterUpdate, which keeps the linked
        // SmartTeamsAppUser in sync.
        const fieldEngineer = new SmartTeamsFieldEngineer({
          ...existingFieldEngineer,
          partnerCompanyIds: [partnerCompanyId],
          name: displayName || email,
          email,
          defaultLocation: { city: record.city || undefined, country: record.country || undefined },
          firstLanguage: localLanguage,
          secondLanguage,
        });
        if (userId) fieldEngineer.userId = userId;
        // Only ever set the ServiceNow link, never clear it — a CSV without the column (or with an
        // empty cell) must leave an existing link alone.
        if (record.serviceNowSysId) fieldEngineer.serviceNowSysId = record.serviceNowSysId;
        // Only "partner" changes an existing flag. A blank cell and "internal" say nothing, so they never undo a flag
        // set by hand or by an earlier import (it is preserved by the spread above).
        if (engineerType.isPartner) fieldEngineer.isPartner = true;
        await fieldEngineer.save();
        updatedCount += 1;
        messages.push(
          `Row ${rowNumber}: updated field engineer (${email})` +
            (record.serviceNowSysId && record.serviceNowSysId !== existingFieldEngineer.serviceNowSysId
              ? ` — linked to ServiceNow ${record.serviceNowSysId}`
              : ''),
        );
      } else {
        const fieldEngineer = new SmartTeamsFieldEngineer({
          partnerCompanyIds: [partnerCompanyId],
          name: displayName || email,
          email,
          defaultLocation: { city: record.city || undefined, country: record.country || undefined },
          firstLanguage: localLanguage,
          secondLanguage,
          serviceNowSysId: record.serviceNowSysId || undefined,
          userId,
          mustChangePassword: true,
          // A new engineer starts internal; only a "partner" cell makes them a partner.
          isPartner: engineerType.isPartner ?? false,
        });

        const fieldEngineerId = await fieldEngineer.create();
        createdCount += 1;
        if (fieldEngineer.isPartner) messages.push(`Row ${rowNumber}: created as a partner engineer`);

        if (userId) {
          await ensureAppUserForFieldEngineer({
            userId,
            email,
            primaryLanguage: fieldEngineer.firstLanguage ?? undefined,
            serviceNowFEId: fieldEngineer.serviceNowSysId ?? undefined,
            companyId: partnerCompanyId,
          });
        }

        try {
          const notificationMessage = await sendFieldEngineerOnboardingEmail(
            fieldEngineerId,
            passwordApplied ? record.password : undefined,
          )();
          messages.push(`Row ${rowNumber}: ${notificationMessage}`);
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'unknown error';
          messages.push(`Row ${rowNumber}: onboarding email failed (${errorMessage})`);
        }
      }

      if (courseId && userId) {
        const existingEnrollment = await enrollmentCollection.findOne({ courseId, userId });
        if (existingEnrollment) {
          messages.push(`Row ${rowNumber}: already enrolled in selected course`);
        } else {
          const enrollment = new LMSCourseEnrollment({ courseId, userId });
          await enrollment.create();
          messages.push(`Row ${rowNumber}: enrolled in selected course`);
        }
      }
    }

    return [
      `Imported ${createdCount} field engineers`,
      `Updated ${updatedCount} field engineers`,
      `Skipped ${skippedCount} rows`,
      ...messages,
    ];
  },
});
